import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FlashMessageCreate from './FlashMessage';
import Input from './Input/Input';

import API from '../API';
import { FlashMessageType } from '../const';
import { FormValuesFromFields, InputFields, RawField, withValidation } from '../forms';

type TRegistrationModalLink = {
  isDisabled: boolean;
  setIsDisabled: (value: boolean) => void;
  setBeforeClose: (value: { flag: boolean; message: string }) => void;
  removeModal: (value: boolean) => void;
};

const RegistrationModalLink: FC<TRegistrationModalLink> = ({ isDisabled, setIsDisabled, setBeforeClose, removeModal }) => {
  const FormFields = {
    email: { ...InputFields.email, required: true },
  } as const satisfies Record<string, RawField>;

  type RegistrationFormValues = FormValuesFromFields<typeof FormFields>;

  const imgWrapper = useRef<HTMLDivElement>(document.querySelector('.registration-modal__img'));
  const [imgHeight, setImgHeight] = useState('5px');
  const methods = useForm<RegistrationFormValues>({
    defaultValues: {
      email: '',
    },
  });
  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    getValues,
    control,
  } = methods;

  const onSubmit = withValidation(FormFields, methods, () => {
    setIsDisabled(true);

    const formData = new FormData();
    let message = '';
    let title = '';
    let type = FlashMessageType.Success;

    for (const [key, value] of Object.entries(getValues())) {
      formData.set(key, value);
    }

    API.sendEmail(formData)
      .then(({ data }) => {
        message = data.msgUser;
        title = data.msgUserTitle || '';
        type = data.msgType;
        reset({ email: '' });
        removeModal(true); // Optional
      })
      .catch((err) => {
        message = err.response.data.msgUser;
        title = err.response.data.msgUserTitle || '';
        type = err.response.data.msgType;
      })
      .finally(() => {
        FlashMessageCreate({ title, message, type, isSelfDestroyable: true });
        setIsDisabled(false);
      });
  });

  useEffect(() => {
    if (isDirty) {
      setBeforeClose({ flag: true, message: 'メールはまだ送信されていません。やめますか？' });
    }

    return () => {
      setBeforeClose({ flag: false, message: '' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // Fix image on render
  useEffect(() => {
    setImgHeight(`${imgWrapper.current?.clientHeight}px`);
  }, [imgWrapper]);

  return (
    <div className='registration-modal__link'>
      <form className='registration-modal__link-form' onSubmit={handleSubmit(onSubmit)}>
        <h5>URLをメールで送信する</h5>
        <Controller
          name={FormFields.email.name}
          control={control}
          rules={{ required: FormFields.email.required }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label={FormFields.email.label}
              errorText={fieldState.error?.message}
              disabled={isDisabled}
              inputProps={{
                tabIndex: FormFields.email.tabIndex,
                required: FormFields.email.required,
                maxLength: FormFields.email.maxLength,
                pattern: FormFields.email.pattern,
              }}
              type={FormFields.email.type}
              size='small'
              fullWidth
            />
          )}
        />
        <button className={`btn btn--main ${isDisabled ? 'btn--loading' : ''}`} type='submit' disabled={isDisabled}>
          登録フォームの送信
        </button>
      </form>
      <hr />
      <div className='registration-modal__img' ref={imgWrapper}>
        <h5>登録用URLをQRコードで読み取る</h5>
        <a href={API.getQrcode()} target='_blank' rel='noreferrer'>
          <img width={imgHeight} src={API.getQrcode(0)} alt='' />
        </a>
      </div>
    </div>
  );
};

export default RegistrationModalLink;
